import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Checkbox, Input, message } from 'antd'
import type { TextAreaRef } from 'antd/es/input/TextArea'
import exclamationIcon from 'assets/images/exclamation-circle-blue-22.svg'
import toolbarBackSVG from 'assets/images/toolbar-back.svg'
import imgButtonImportantChecked from 'assets/images/todo/icon-important-checked.svg'
import imgButtonImportant from 'assets/images/todo/icon-important.svg'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { getSortedFolders, getUserInfo } from 'pages/todo/modules/TodoFolderSync'
import * as todoManager from 'pages/todo/modules/TodoManager'
import { enableSync } from 'pages/todo/modules/TodoSync'
import settings from 'popups/Settings'
import { useTranslation } from 'react-i18next'
import { RRule } from 'rrule'
import { confirmBase } from 'utils/ui'
import { PRIORITY, TODO_STATUS, getReminderTime } from '../../modules/TodoItem'
import type { Priority, TaskReminder, TodoItem, TodoStatus } from '../../modules/TodoItem'
import type { TodoFolder } from '../../modules/TodoFolder'
import { getCurrentRecurrenceDescription } from '../../utils/recurrenceUtils'
import { getDefaultDueDate, getDefaultFolder } from './todoEditorUtils'
import DescriptionSelector from './DescriptionSelector'
import DueDateSelector from './DueDateSelector'
// import type { DueDateEditData } from './DueDateSelector'
import FolderSelector from './FolderSelector'
import PrioritySelector from './PrioritySelector'
import RecurrenceDescriptionDisplay from './RecurrenceDescriptionDisplay'
import ReminderSelector, { calculateReminderDateTime } from './ReminderSelector'
import type { ReminderAlarm, ReminderSelectorRef } from './ReminderSelector'
import styles from './style.module.scss'

const { TextArea } = Input

export function modalTodoEditor(todo: Partial<TodoItem> | null, duplicateTodo: boolean = false) {
  const modalObj = confirmBase({
    wrapClassName: styles.todoEditorPopup,
    className: 'mobile-fullpage',
    width: 480,
    maskClosable: false,
    closable: false,
    keyboard: false,
    content: (
      <TodoEditor
        todo={todo}
        duplicateTodo={duplicateTodo}
        onSave={() => {
          modalObj.destroy()
        }}
        onClose={() => modalObj.destroy()}
      />
    ),
    footer: null,
  })

  return modalObj
}

export { getDefaultDueDate, getDefaultFolder }

interface TodoEditorProps {
  todo: Partial<TodoItem> | null
  duplicateTodo?: boolean
  onSave: (todo: Partial<TodoItem>) => void
  onClose: () => void
}

interface EditData {
  summary: string
  description: string
  status: TodoStatus
  priority: Priority
  percentComplete: number
  startDate: Date | null
  dueDate: Date | null
  createdDate: Date | null
  lastModifiedDate: Date | null
  location: string
  alarms: ReminderAlarm[]
  recurrenceRule: RRule | null
  datetype: string
  folder: TodoFolder | null
}
const TodoEditor: React.FC<TodoEditorProps> = ({ todo, duplicateTodo, onSave, onClose }) => {
  const { t } = useTranslation()
  const isMobileView = settings.getStore().getState().appsettings.isMobileView
  const [editData, setEditData] = useState<EditData | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const textareaRef = useRef<TextAreaRef>(null)
  const reminderSelectorRef = useRef<ReminderSelectorRef>(null)

  const folders = getSortedFolders()
  const todoSettings = settings.getValue('todo') || {}
  const defaultDueDate = todoSettings.defaultDueDate || 'today'
  const defaultFolder = todoSettings.defaultFolder || null
  const lastAddedFolder = todoSettings.lastAddedFolder || null
  // const { timeFormat: settingTimeFormat } = settings.getStore().getState()
  // const is12HourFormat = settingTimeFormat === 'h:mm A'
  // const formattedTime = is12HourFormat ? format12Hours : format24Hours

  const [dueDate, setDueDate] = useState<Date | null>(null)

  const orgTodo = useRef<EditData | null>(null)

  // Helper function to create initial alarm and related data if needed
  const createInitialAlarmData = (todoDueDate: Date | null, isNew: boolean) => {
    // Only auto-create alarm for new todos (when todo.url is null)
    if (!isNew || !todoDueDate) {
      return {
        alarms: [],
        startDate: null,
        datetype: 'date',
      }
    }

    // Get settings with proper defaults (same as ReminderSelector)
    const defaultReminderTimeForFuture = todoSettings.defaultReminderTimeForFuture || 'dueDate'
    const defaultReminderTime = todoSettings.defaultReminderStartTime || 540 // 9AM (540 minutes)
    const defaultReminderTimeForToday = todoSettings.defaultReminderTimeForToday || 0

    // Calculate the reminder date-time based on settings
    const reminderDateTime = calculateReminderDateTime(
      todoDueDate,
      defaultReminderTimeForFuture,
      defaultReminderTime,
      defaultReminderTimeForToday,
    )

    if (reminderDateTime) {
      // Format the time the same way ReminderSelector does
      const formattedTime = dayjs(reminderDateTime).utc().format('YYYYMMDD[T]HHmmss[Z]')

      const alarms = [
        {
          action: 'DISPLAY',
          trigger: {
            params: {},
            val: formattedTime,
          },
        },
      ]

      // Return alarm data with startDate and datetype (same logic as ReminderSelector)
      return {
        alarms,
        startDate: reminderDateTime, // Set startDate to reminder time
        datetype: 'datetime', // Set to datetime when alarms are present
      }
    }

    return {
      alarms: [],
      startDate: null,
      datetype: 'date',
    }
  }

  useEffect(() => {
    if (todo) {
      const sortedFolders = getSortedFolders()

      const defaultFolderFromSettings = getDefaultFolder(
        defaultFolder,
        lastAddedFolder,
        sortedFolders,
      )

      let selectedFolder = !todo.url
        ? // 새 할일인 경우: defaultFolderFromSettings 우선, 없으면 첫 번째 폴더 사용
          defaultFolderFromSettings || null
        : folders.find((f) => f.url === todo.folderUrl) ||
          (sortedFolders.length > 0 ? sortedFolders[0] : null)

      if (duplicateTodo) {
        if (selectedFolder && selectedFolder.isReadOnly === true) {
          selectedFolder = defaultFolderFromSettings
        }
      }

      const todoDueDate = todo.dueDate || (!todo.url ? getDefaultDueDate(defaultDueDate) : null)
      setDueDate(todoDueDate)

      // Determine if this is a new todo (for alarm creation)
      const isNewTodo = !todo.url || duplicateTodo

      // Create initial alarm data - use existing data for existing todos, auto-create for new todos
      let alarmData
      if (isNewTodo) {
        if (duplicateTodo) {
          alarmData = {
            alarms: todo.alarms || [],
            startDate: todo.startDate || null,
            datetype: todo.datetype || 'date',
          }
        } else {
          // For new todos, auto-create alarm based on settings
          alarmData = createInitialAlarmData(todoDueDate, true)
        }
      } else {
        // For existing todos, keep existing data
        alarmData = {
          alarms: todo.alarms || [],
          startDate: todo.startDate || null,
          datetype: todo.datetype || 'date',
        }
      }

      const initialData = {
        summary: todo.summary || '',
        description: todo.description || '',
        status: todo.status || TODO_STATUS.NEEDS_ACTION,
        priority: todo.priority || PRIORITY.MEDIUM,
        percentComplete: todo.percentComplete || 0,
        startDate: alarmData.startDate,
        dueDate: todoDueDate,
        createdDate: todo.createdDate || null,
        lastModifiedDate: todo.lastModifiedDate || null,
        location: todo.location || '',
        alarms: alarmData.alarms,
        recurrenceRule: todo.recurrenceRule
          ? todo.recurrenceRule instanceof RRule
            ? todo.recurrenceRule
            : RRule.fromString(`RRULE:${todo.recurrenceRule}`)
          : null,
        datetype: alarmData.datetype,
        folder: selectedFolder || sortedFolders[0],
      }
      setEditData(initialData)
      orgTodo.current = initialData
    }
  }, [todo, defaultFolder])

  useEffect(() => {
    if (orgTodo.current) {
      if (!editData) return

      const orgData = orgTodo.current

      const hasDataChanged =
        JSON.stringify(editData) !==
        JSON.stringify({
          summary: orgData.summary || '',
          description: orgData.description || '',
          status: orgData.status || TODO_STATUS.NEEDS_ACTION,
          priority: orgData.priority || PRIORITY.MEDIUM,
          percentComplete: orgData.percentComplete || 0,
          startDate: orgData.startDate || null,
          dueDate: orgData.dueDate || null,
          createdDate: orgData.createdDate || null,
          lastModifiedDate: orgData.lastModifiedDate || null,
          location: orgData.location || '',
          alarms: orgData.alarms || [],
          recurrenceRule: orgData.recurrenceRule
            ? orgData.recurrenceRule instanceof RRule
              ? orgData.recurrenceRule
              : RRule.fromString(`RRULE:${orgData.recurrenceRule}`)
            : null,
          datetype: orgData.datetype || 'date',
          folder: orgData.folder,
        })
      setHasChanges(hasDataChanged)
    }
  }, [editData])

  if (!todo) return null

  // Helper function to normalize dates by setting seconds and milliseconds to 0
  const normalizeWithoutSeconds = (d: Date | null) => {
    if (!d) return null
    const nd = new Date(d)
    nd.setSeconds(0, 0) // zero seconds & milliseconds
    return nd
  }

  const handleSave = async () => {
    if (!hasChanges || !editData) return

    try {
      // 1. Prepare normalized due date
      const due = editData.dueDate ? normalizeWithoutSeconds(editData.dueDate) : null

      // 2. Calculate first reminder time (if any)
      let reminderTime = null
      if (editData.alarms && editData.alarms.length > 0) {
        reminderTime = getReminderTime(
          editData.alarms[0] as TaskReminder,
          due?.toISOString?.(),
          editData.startDate,
        )
        reminderTime = normalizeWithoutSeconds(reminderTime)
      }

      // 3. Decide proper startDate based on reminder and dueDate comparison
      let computedStartDate = editData.startDate
      if (reminderTime) {
        if (due && dayjs(reminderTime).isSame(due, 'day')) {
          // reminder와 dueDate가 같은 날(day)이면 startDate를 dueDate로 설정
          computedStartDate = due
        } else {
          // reminder와 dueDate가 다른 날이면 startDate는 reminder date를 따른다
          computedStartDate = reminderTime
        }
      }
      computedStartDate = computedStartDate ? normalizeWithoutSeconds(computedStartDate) : null

      const updatedTodo = {
        ...todo,
        ...editData,
        startDate: computedStartDate,
        dueDate: due,
        // Ensure datetype is set correctly based on alarms
        datetype: editData.alarms && editData.alarms.length > 0 ? 'datetime' : 'date',
      }
      await onSaveTodo(updatedTodo as Partial<TodoItem> & EditData)
    } catch (error) {
      console.error('Save failed:', error)
      message.error(t('todo.editor.saveFailed'))
    }
  }

  const updateFolderSync = (folderUrl: string, isSync: boolean) => {
    enableSync(folderUrl, isSync)
  }

  const onSaveTodo = (updatedTodo: Partial<TodoItem> & EditData) => {
    return new Promise<void>((resolve, reject) => {
      const userInfo = getUserInfo()
      if (!todo.url || duplicateTodo) {
        const newTodo = {
          ...updatedTodo,
          uid: crypto.randomUUID(),
          url: null,
          etag: null,
          folderUrl: editData?.folder?.url || folders[0]?.url,
          dtstamp: new Date(),
          createdDate: new Date(),
          completedDate: null,
          percentComplete: 0,
          author: {
            val: userInfo.email,
            params: {
              CN: userInfo.name,
            },
          },
        }

        if (!duplicateTodo) {
          newTodo.completedDate =
            updatedTodo.status === TODO_STATUS.COMPLETED ? new Date().toISOString() : null
          newTodo.percentComplete = updatedTodo.status === TODO_STATUS.COMPLETED ? 100 : 0
        }

        const targetFolder = folders.find((f) => f.url === newTodo.folderUrl) || folders[0]

        todoManager.createTask(newTodo as TodoItem, targetFolder, (success) => {
          if (success) {
            message.success(t('todo.editor.taskSaved'))
            if (!targetFolder.isSync) {
              updateFolderSync(targetFolder.url, true)
            }
            onSave(updatedTodo)
            if (onClose) onClose()
            resolve()
          } else {
            message.error(t('todo.editor.saveFailed'))
            reject(new Error('Create failed'))
          }
        })
      } else {
        const todoToUpdate = {
          ...updatedTodo,
          completedDate:
            updatedTodo.status === TODO_STATUS.COMPLETED ? new Date().toISOString() : null,
          percentComplete: updatedTodo.status === TODO_STATUS.COMPLETED ? 100 : 0,
          author: {
            val: userInfo.email,
            params: {
              CN: userInfo.name,
            },
          },
        }

        const currentFolderUrl = editData?.folder?.url || folders[0]?.url
        const originalFolderUrl = todo.folderUrl
        const moveToFolder =
          currentFolderUrl !== originalFolderUrl
            ? folders.find((f) => f.url === currentFolderUrl)
            : null

        todoManager.updateTask(
          todo.url!,
          todo.etag!,
          todoToUpdate as TodoItem,
          moveToFolder,
          (success) => {
            if (success) {
              message.success(t('todo.editor.taskSaved'))
              onSave(updatedTodo)
              if (onClose) onClose()
              resolve()
            } else {
              message.error(t('todo.editor.saveFailed'))
              reject(new Error('Update failed'))
            }
          },
        )
      }
    })
  }

  const confirmOpenRef = useRef<boolean>(false)

  const handleClose = useCallback(() => {
    if (!hasChanges) {
      if (onClose) onClose()
      return
    }

    // Prevent multiple confirmBase executions
    if (confirmOpenRef.current) {
      return
    }
    confirmOpenRef.current = true

    confirmBase({
      icon: <img src={exclamationIcon} alt="Exclamation" />,
      title: t('calendar.result.changesTitle'),
      content: t('calendar.result.changesContent'),
      okText: t('common.button.ok'),
      cancelText: t('common.button.cancel'),
      closeIcon: false,
      onOk: () => {
        confirmOpenRef.current = false
        if (onClose) onClose()
      },
      onCancel: () => {
        confirmOpenRef.current = false
      },
      closable: false,
      maskClosable: false,
      keyboard: false,
      centered: true,
    })
  }, [hasChanges, onClose, t])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [handleClose])

  const handleTitleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditData((prev) => (prev ? { ...prev, summary: e.target.value } : prev))
  }

  const handleStatusChange = (status: TodoStatus) => {
    setEditData((prev) => (prev ? { ...prev, status } : prev))
  }

  const handlePriorityChange = (priority: Priority) => {
    setEditData((prev) => (prev ? { ...prev, priority } : prev))
  }

  const handleStartDateChange = (date: Date | null) => {
    setEditData((prev) => (prev ? { ...prev, startDate: date } : prev))
  }

  const handleDueDateChange = (date: Date | null, rrule: RRule | null) => {
    if (reminderSelectorRef.current?.notifyUserDueDateChange) {
      reminderSelectorRef.current.notifyUserDueDateChange()
    }

    setEditData((prev) => {
      if (!prev) return prev
      setDueDate(date)
      return { ...prev, dueDate: date, recurrenceRule: rrule }
    })
  }

  const handleDescriptionChange = (description: string) => {
    setEditData((prev) => (prev ? { ...prev, description } : prev))
  }

  const handleReminderChange = (alarms: ReminderAlarm[]) => {
    setEditData((prev) =>
      prev
        ? {
            ...prev,
            alarms,
            datetype: alarms && alarms.length > 0 ? 'datetime' : 'date',
          }
        : prev,
    )
  }

  const handleFolderChange = (folder: TodoFolder | null) => {
    setEditData((prev) => (prev ? { ...prev, folder } : prev))
  }

  /**
   * RRULE이 정의되어 있을 때는 반드시 DTSTART가 있어야 한다.
   * 반복은 DTSTART를 기준으로 RRULE 규칙이 적용되기 때문에, DTSTART 없이 반복은 의미가 없다.
   * RFC 5545 명세에서는 반복 규칙이 있을 때 DTSTART는 필수(required) 이다.
   * @param {*} recurrenceRule : RRule 객체
   */
  const handleRecurrenceChange = (recurrenceRule: RRule | null) => {
    setEditData((prev) => {
      if (!prev) return prev
      if (recurrenceRule) {
        // RRULE이 설정되면 DTSTART도 반드시 설정해야 함 (RFC 5545)
        const startDate = prev.startDate || prev.dueDate || new Date()
        // dueDate가 설정되지 않았다면 startDate와 같은 값으로 설정
        const dueDate = prev.dueDate || startDate

        setDueDate(dueDate)
        // datetype을 'date'로 설정하여 DATE 형식으로
        return { ...prev, recurrenceRule, startDate, dueDate, datetype: 'date' }
      } else {
        setDueDate(null)
        // RRULE이 제거되면 startDate와 dueDate도 null로 설정
        return { ...prev, recurrenceRule: null, startDate: null, dueDate: null }
      }
    })
  }

  const folderColor = editData?.folder?.calendarColor?._cdata || '#fd4c45'

  useEffect(() => {
    if (isMobileView) {
      const el = document.querySelector('#todo-editor-container')
      if (el) {
        let height = 0
        let detailsEl: HTMLElement | null = null
        el.childNodes.forEach((child) => {
          const htmlChild = child as HTMLElement
          if (htmlChild.classList && htmlChild.classList.contains('todo-content-details')) {
            detailsEl = htmlChild
          } else if (htmlChild.clientHeight) {
            height += htmlChild.clientHeight
          }
        })
        if (detailsEl) {
          detailsEl.style.setProperty(
            '--details-max-height',
            `calc(${el.clientHeight}px - ${height}px)`,
          )
        }
      }
    }
  }, [])

  const getHeader = () => {
    if (isMobileView) {
      return (
        <div className={styles.mobileHeader}>
          <Button
            type="text"
            icon={<img src={toolbarBackSVG} alt="back" />}
            onClick={handleClose}
          />
          <div className={styles.mobileTitle}>
            {!todo.url || duplicateTodo ? t('todo.editor.newTask') : t('todo.editor.editTask')}
          </div>
        </div>
      )
    } else {
      return (
        <div className={styles.content}>
          <div className={styles.taskTitleHeader}>
            <h2 className={styles.taskTitle}>
              {!todo.url || duplicateTodo ? t('todo.editor.newTask') : t('todo.editor.editTask')}
            </h2>
          </div>
        </div>
      )
    }
  }

  return (
    <div className={styles.editContainer} id="todo-editor-container" data-testid="todo-editor">
      {getHeader()}

      <div className={classNames(styles.details, 'todo-content-details')}>
        {/* 제목 입력 */}
        <div className={styles.titleInputAndCheckBoxWrapper}>
          <div className={styles.checkBoxArea}>
            <Checkbox
              checked={editData?.status === TODO_STATUS.COMPLETED}
              onChange={(e) =>
                handleStatusChange(
                  e.target.checked ? TODO_STATUS.COMPLETED : TODO_STATUS.NEEDS_ACTION,
                )
              }
              className={styles.checkBox}
              // check box color 는 folderColor 이다.
              style={{ '--checkbox-color': folderColor } as React.CSSProperties}
            />
          </div>
          <TextArea
            maxLength={100}
            ref={textareaRef}
            className={styles.titleInput}
            placeholder={t('email.placeholder.subject')}
            value={editData?.summary || ''}
            onChange={handleTitleChange}
            autoSize={{ minRows: 1, maxRows: 100 }}
          />
          <div
            className={styles.importantBtn}
            onClick={(e) => {
              e.stopPropagation()
              handlePriorityChange(
                editData?.priority === PRIORITY.HIGH ? PRIORITY.MEDIUM : PRIORITY.HIGH,
              )
            }}
          >
            <img
              src={
                editData?.priority === PRIORITY.HIGH
                  ? imgButtonImportantChecked
                  : imgButtonImportant
              }
              alt={editData?.priority === PRIORITY.HIGH ? 'important-checked' : 'important'}
            />
          </div>
        </div>

        {/* Folder Selector */}
        <FolderSelector
          selectedFolder={editData?.folder ?? null}
          folders={folders}
          onFolderChange={handleFolderChange}
          disabled={false}
        />

        {/* DueDate Selector */}
        <DueDateSelector
          dueDate={dueDate}
          onDueDateChange={handleDueDateChange}
          disabled={false}
          editData={
            editData
              ? {
                  dueDate: editData.dueDate,
                  recurrenceRule: editData.recurrenceRule
                    ? {
                        recurrenceRule: editData.recurrenceRule,
                        options: editData.recurrenceRule.options,
                        origOptions: editData.recurrenceRule.origOptions,
                      }
                    : null,
                }
              : null
          }
          onRecurrenceChange={handleRecurrenceChange}
        />
        {/* dueDate 가 recurrence rule 인 경우 rrule string 을 표시. */}
        {editData?.recurrenceRule && (
          <RecurrenceDescriptionDisplay
            description={getCurrentRecurrenceDescription(editData?.recurrenceRule, t)}
          />
        )}
        <ReminderSelector
          ref={reminderSelectorRef}
          value={editData?.alarms ?? null}
          onChange={handleReminderChange}
          disabled={false}
          dueDate={editData?.dueDate ?? null}
          startDate={editData?.startDate}
          onDueChange={(date) => handleDueDateChange(date, null)}
          onStartDateChange={handleStartDateChange}
          isNew={!todo.url}
        />

        {/* Priority Selector */}
        <PrioritySelector
          value={editData?.priority ?? PRIORITY.MEDIUM}
          onChange={handlePriorityChange}
          disabled={false}
        />

        {/* Description Selector */}
        <DescriptionSelector value={editData?.description} onChange={handleDescriptionChange} />
      </div>

      {/* footer - save, cancel, delete button */}
      <div className={styles.footer}>
        {/* todo url 이 있을 때만 dlete 표시 */}
        <div className={styles.left} />
        <div className={styles.right}>
          <button className={styles.cancelButton} onClick={handleClose} style={{ minWidth: 100 }}>
            {t('common.button.cancel')}
          </button>
          <button
            className={`${styles.saveButton} ${!hasChanges ? styles.disabled : ''}`}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleSave()
            }}
            disabled={!hasChanges}
          >
            {t('common.button.save')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default TodoEditor
